/**
 * Ícones da interface — desenhados aqui, não importados.
 *
 * ── Por que não uma biblioteca ────────────────────────────────────────────
 *
 * São poucos ícones. Uma biblioteca traria mil e a decisão de qual estilo usar
 * já estaria tomada por outra pessoa; nove paths de traço em 20×20 são menos
 * código que o import dela e ficam com a MESMA linguagem: traço 1.7, pontas
 * redondas, geometria simples. Ícone de origem misturada é o detalhe que faz
 * uma interface parecer montada de partes.
 *
 * `currentColor` de propósito: o ícone herda a cor do item de menu e todos os
 * estados (repouso, hover, ativo, desabilitado) saem de graça.
 */

import type { ReactNode } from 'react';

function Icone({ children }: { children: ReactNode }): ReactNode {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/* ── Os do editor de conversa ──────────────────────────────────────────────
 *
 * Moram aqui, e não junto do editor, pela razão do cabeçalho: um vocabulário
 * só. Eles nasceram como emoji — 📎 🎤 🙂 — e emoji não é ícone: muda de forma
 * em cada sistema, não herda a cor do botão, e no meio de nove traços de 1.7
 * parece um adesivo colado.
 */

/** Clipe — anexar arquivo ou foto. */
export const IconeAnexo = (): ReactNode => (
  <Icone>
    <path d="M15.3 9.2 9.6 14.9a3.2 3.2 0 0 1-4.5-4.5l6-6a2.1 2.1 0 0 1 3 3l-6 6a1 1 0 0 1-1.4-1.4l5.3-5.3" />
  </Icone>
);

/* ── Confirmação de entrega ────────────────────────────────────────────────
 *
 * O vocabulário é o do WhatsApp, e isso é decisão de produto, não imitação: o
 * vendedor já lê esses três estados dezenas de vezes por dia no telefone dele.
 * Reinventá-los custaria treinamento para não ganhar nada.
 *
 *   um traço cinza    saiu daqui
 *   dois traços cinza chegou no aparelho
 *   dois traços azuis foi lido
 *
 * Eles NÃO são texto com `✓`: o caractere muda de forma e de largura em cada
 * sistema, e no Windows sai como um visto fino que some ao lado do horário.
 *
 * `viewBox` mais largo que alto porque o desenho é deitado — dois vistos lado a
 * lado no mesmo espaço vertical de uma linha de 10 px.
 */

function Tick({ duplo }: { duplo: boolean }): ReactNode {
  return (
    <svg
      width={duplo ? 16 : 11}
      height="11"
      viewBox={duplo ? '0 0 16 11' : '0 0 11 11'}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 6.2 3.7 9 9.4 2.4" />
      {/* O segundo visto sai deslocado, e não sobreposto: sobrepostos viram
          uma mancha em tela de 100% e não dá para contar quantos são — que é
          exatamente a informação. */}
      {duplo && <path d="M6.6 6.2 9.3 9 15 2.4" />}
    </svg>
  );
}

/** Um traço: a mensagem saiu do nosso lado. */
export const IconeEnviada = (): ReactNode => <Tick duplo={false} />;

/** Dois traços: o canal confirmou a entrega no aparelho. */
export const IconeEntregue = (): ReactNode => <Tick duplo />;

/** Dois traços — o azul quem dá é o CSS, pelo estado da bolha. */
export const IconeLida = (): ReactNode => <Tick duplo />;

/**
 * Falha na entrega.
 *
 * Não é um visto vermelho: visto significa "deu certo" no vocabulário que esta
 * família estabelece, e um visto vermelho obrigaria a pessoa a interpretar a
 * cor antes da forma. O círculo com a exclamação é diferente ao primeiro olhar,
 * inclusive para quem não distingue vermelho de cinza.
 */
export const IconeFalhaDeEntrega = (): ReactNode => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="10" cy="10" r="8" />
    <path d="M10 5.6v5M10 13.6v.6" />
  </svg>
);
